// ============================================================================
// OBT F3 — Müdür nihai park karar seti (taslak + kesinleştirme)
// ============================================================================
// Aday evreni: Müdür kuyruğundaki (my_approved + müdür-durumları) cevaplar —
// yani MY tarafından müdüre iletilmiş parklar. Serbest park arama/ekleme YOKTUR
// (backend karar item'ları response_id anahtarlıdır; aday olmayan park eklenemez).
//
// Karar seti sürümlüdür; taslakta item eklenip çıkarılabilir, kesinleştirmede
// DONAR. Backend optimistik `expected_version` KULLANMAZ (pesimistik kilit +
// durum-guard); 409/invalid-state → mesaj + reload.
// ============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  listDirectorQueue,
  getDecisionSetHistory,
  createDecisionSet,
  upsertDecisionItem,
  removeDecisionItem,
  finalizeDecisionSet,
  reviseDecisionSet,
  addDirectorPark,
  removeDirectorPark,
} from '../reviewApi';
import { normalizeReviewError } from '../reviewErrors';

function isCanceled(error) {
  return (
    error?.code === 'ERR_CANCELED' ||
    error?.name === 'CanceledError' ||
    error?.name === 'AbortError'
  );
}

/** Sürümler arasından AKTİF seti seç: taslak varsa o, yoksa en yüksek sürüm. */
function pickActiveSet(sets) {
  const list = Array.isArray(sets) ? sets : [];
  const draft = list.find((s) => s.status === 'draft');
  if (draft) return draft;
  return list
    .slice()
    .sort((a, b) => (b.version_number || 0) - (a.version_number || 0))[0] || null;
}

/**
 * @param {string|null} campaignId
 * @param {{enabled?: boolean}} options
 */
export function useFinalParkSelection(campaignId, { enabled = true } = {}) {
  const [state, setState] = useState({
    loading: Boolean(campaignId) && enabled,
    error: null,
    candidates: [],
    decisionSet: null,
  });
  const [actionError, setActionError] = useState(null);
  const [actionPending, setActionPending] = useState(false);
  const abortRef = useRef(null);

  const load = useCallback(async () => {
    if (!campaignId || !enabled) {
      setState({ loading: false, error: null, candidates: [], decisionSet: null });
      return;
    }
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const [candidates, history] = await Promise.all([
        listDirectorQueue({ campaignId }, { signal: controller.signal }),
        getDecisionSetHistory(campaignId, { signal: controller.signal }).catch(() => null),
      ]);
      if (controller.signal.aborted) return;
      // Backend DecisionSetHistoryOut serializes the versions under `versions`
      // (not `items`); reading the wrong key left the workspace unable to see the
      // draft it just created, so the Director could never decide/finalize in the UI.
      const sets = Array.isArray(history?.versions) ? history.versions : [];
      setState({
        loading: false,
        error: null,
        candidates,
        decisionSet: pickActiveSet(sets),
      });
    } catch (error) {
      if (isCanceled(error) || controller.signal.aborted) return;
      setState({
        loading: false,
        error: normalizeReviewError(error),
        candidates: [],
        decisionSet: null,
      });
    }
  }, [campaignId, enabled]);

  useEffect(() => {
    load();
    return () => abortRef.current?.abort();
  }, [load]);

  const run = useCallback(
    async (fn) => {
      if (actionPending) return false;
      setActionPending(true);
      setActionError(null);
      try {
        await fn();
        await load();
        setActionPending(false);
        return true;
      } catch (error) {
        if (isCanceled(error)) {
          setActionPending(false);
          return false;
        }
        const normalized = normalizeReviewError(error);
        setActionError(normalized);
        if (normalized.status === 409 || normalized.status === 422) await load();
        setActionPending(false);
        throw error;
      }
    },
    [actionPending, load]
  );

  const draftId =
    state.decisionSet?.status === 'draft' ? state.decisionSet.decision_set_id : null;

  const createDraft = useCallback(
    (note) => run(() => createDecisionSet({ campaignId, note })),
    [run, campaignId]
  );
  const setDecision = useCallback(
    (responseId, decision, explanation) =>
      run(() => {
        if (!draftId) throw new Error('no-draft');
        return upsertDecisionItem(draftId, { responseId, decision, explanation });
      }),
    [run, draftId]
  );
  const clearDecision = useCallback(
    (responseId) =>
      run(() => {
        if (!draftId) throw new Error('no-draft');
        return removeDecisionItem(draftId, { responseId });
      }),
    [run, draftId]
  );
  const finalize = useCallback(
    (decisionText) =>
      run(() => {
        if (!draftId) throw new Error('no-draft');
        return finalizeDecisionSet(draftId, { decisionText });
      }),
    [run, draftId]
  );
  const revise = useCallback(
    (note) => run(() => reviseDecisionSet({ campaignId, note })),
    [run, campaignId]
  );
  // Müdür, aday kümesi dışından park ekler (taslak zorunlu). Sunucu 409/422/403
  // döndürebilir; `run` bunları normalize eder + reload eder.
  const addDirectorParkItem = useCallback(
    (parkId, reason) =>
      run(() => {
        if (!draftId) throw new Error('no-draft');
        return addDirectorPark(draftId, { parkId, reason });
      }),
    [run, draftId]
  );
  const removeDirectorParkItem = useCallback(
    (itemId) =>
      run(() => {
        if (!draftId) throw new Error('no-draft');
        return removeDirectorPark(draftId, itemId);
      }),
    [run, draftId]
  );

  return {
    ...state,
    reload: load,
    draftId,
    actionError,
    actionPending,
    clearActionError: () => setActionError(null),
    createDraft,
    setDecision,
    clearDecision,
    finalize,
    revise,
    addDirectorParkItem,
    removeDirectorParkItem,
  };
}
